import { useCallback, useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import type { OtpChannel, SendOtp } from '@/modules/auth/use-cases'
import {
  EmailLinkedToDifferentAccountError,
  NetworkError,
  OtpSendFailedError,
  PhoneLinkedToDifferentAccountError,
  UserNotFoundError,
  isOtpSendRefusal,
} from '@/shared/domain'
import { isPhoneLoginEnabled } from '@/shared/config'
import { useOtpSendCooldown, useSubmitGuard } from '@/shared/ui/hooks'
import { isValidEmail } from '@/modules/auth/ui/validation'
import { REGISTER_OTP_STATE_STORAGE_KEY } from '../types'

export type AuthSubStep = 'identifier' | 'otp'

export type AuthMode = Extract<OtpChannel, 'email' | 'phone'>

const USER_NOT_FOUND_COOLDOWN_SECONDS = 8

type PersistedOtpState = {
  identifier: string
  channel: AuthMode
  sentAt: number
}

function readPersistedState(): PersistedOtpState | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(REGISTER_OTP_STATE_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<PersistedOtpState>
    if (typeof parsed.identifier !== 'string' || parsed.identifier === '') return null
    if (parsed.channel !== 'email' && parsed.channel !== 'phone') return null
    return {
      identifier: parsed.identifier,
      channel: parsed.channel,
      sentAt: typeof parsed.sentAt === 'number' ? parsed.sentAt : Date.now(),
    }
  } catch {
    return null
  }
}

function writePersistedState(state: PersistedOtpState): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(REGISTER_OTP_STATE_STORAGE_KEY, JSON.stringify(state))
  } catch {
    return
  }
}

function clearPersistedState(): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(REGISTER_OTP_STATE_STORAGE_KEY)
  } catch {
    return
  }
}

export type UseAuthStepProps = {
  onSendOtp: SendOtp
}

export type UseAuthStepResult = {
  subStep: AuthSubStep
  currentMode: AuthMode
  email: string
  phoneE164: string
  identifier: string
  isPhoneLoginEnabled: boolean
  isSubmitting: boolean
  cooldownSeconds: number
  isCooldownActive: boolean
  errorMessage: string
  fieldError: string
  setMode: (m: AuthMode) => void
  setEmail: (v: string) => void
  setPhone: (e164: string, valid: boolean) => void
  handleBlur: () => void
  handleSubmit: (e: FormEvent) => void
  backToIdentifier: () => void
  handleVerifySuccess: () => Promise<void>
}

export function useAuthStep({ onSendOtp }: UseAuthStepProps): UseAuthStepResult {
  const persistedRef = useRef<PersistedOtpState | null>(readPersistedState())
  const persisted = persistedRef.current

  const [subStep, setSubStep] = useState<AuthSubStep>(persisted ? 'otp' : 'identifier')
  const [currentMode, setCurrentMode] = useState<AuthMode>(persisted?.channel ?? 'email')
  const [email, setEmail] = useState<string>(
    persisted && persisted.channel === 'email' ? persisted.identifier : '',
  )
  const [phoneE164, setPhoneE164] = useState<string>(
    persisted && persisted.channel === 'phone' ? persisted.identifier : '',
  )
  const [phoneValid, setPhoneValid] = useState<boolean>(false)
  const { isSubmitting, runGuarded } = useSubmitGuard()
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [hasTouched, setHasTouched] = useState<boolean>(false)
  const [fieldError, setFieldError] = useState<string>('')
  const [cooldownSeconds, setCooldownSeconds] = useState<number>(0)

  useEffect(() => {
    if (cooldownSeconds <= 0) return
    const timer = setTimeout(() => {
      setCooldownSeconds((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)
    return () => clearTimeout(timer)
  }, [cooldownSeconds])

  const phoneEnabled = isPhoneLoginEnabled()
  const identifier = currentMode === 'phone' ? phoneE164 : email.trim()
  const {
    secondsLeft: sendCooldownSeconds,
    applyRefusal,
    recordSend,
  } = useOtpSendCooldown({
    channel: currentMode,
    identifier,
  })
  const activeCooldownSeconds = Math.max(cooldownSeconds, sendCooldownSeconds)

  const validateField = useCallback(
    (mode: AuthMode, emailValue: string, phoneValue: string, phoneIsValid: boolean): string => {
      if (mode === 'phone') {
        if (phoneValue === '' || !phoneIsValid) return 'Enter a valid phone number'
        return ''
      }
      const trimmed = emailValue.trim()
      if (trimmed === '') return 'Enter your email'
      if (!isValidEmail(trimmed)) return 'Enter a valid email'
      return ''
    },
    [],
  )

  const updateEmail = useCallback(
    (v: string) => {
      setEmail(v)
      setErrorMessage('')
      if (hasTouched) {
        setFieldError(validateField('email', v, phoneE164, phoneValid))
      }
    },
    [hasTouched, phoneE164, phoneValid, validateField],
  )

  const setPhone = useCallback(
    (e164: string, valid: boolean) => {
      setPhoneE164(e164)
      setPhoneValid(valid)
      setErrorMessage('')
      if (hasTouched) {
        setFieldError(validateField('phone', email, e164, valid))
      }
    },
    [email, hasTouched, validateField],
  )

  const setMode = useCallback((m: AuthMode) => {
    setCurrentMode(m)
    setErrorMessage('')
    setFieldError('')
    setHasTouched(false)
  }, [])

  const handleBlur = useCallback(() => {
    setHasTouched(true)
    setFieldError(validateField(currentMode, email, phoneE164, phoneValid))
  }, [currentMode, email, phoneE164, phoneValid, validateField])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (activeCooldownSeconds > 0) return
    setHasTouched(true)
    const validationError = validateField(currentMode, email, phoneE164, phoneValid)
    if (validationError !== '') {
      setFieldError(validationError)
      return
    }
    setFieldError('')
    setErrorMessage('')
    await runGuarded(async () => {
      try {
        await onSendOtp({ identifier, channel: currentMode, allowCreate: true })
        recordSend()
        writePersistedState({
          identifier,
          channel: currentMode,
          sentAt: Date.now(),
        })
        setSubStep('otp')
      } catch (err) {
        if (err instanceof UserNotFoundError) {
          setErrorMessage(
            currentMode === 'phone'
              ? 'No account found for this phone number. Contact your club manager for an invitation.'
              : 'No account found for this email. Contact your club manager for an invitation.',
          )
          setCooldownSeconds(USER_NOT_FOUND_COOLDOWN_SECONDS)
        } else if (
          err instanceof EmailLinkedToDifferentAccountError ||
          err instanceof PhoneLinkedToDifferentAccountError
        ) {
          setErrorMessage(err.message)
        } else if (isOtpSendRefusal(err)) {
          setErrorMessage(err.message)
          applyRefusal(err)
        } else if (err instanceof OtpSendFailedError) {
          setErrorMessage(err.message)
        } else if (err instanceof NetworkError) {
          setErrorMessage('Network error. Please try again.')
        } else {
          setErrorMessage('Something went wrong. Please try again.')
        }
      }
    })
  }

  const backToIdentifier = useCallback(() => {
    clearPersistedState()
    setSubStep('identifier')
    setErrorMessage('')
    setFieldError('')
    setHasTouched(false)
  }, [])

  const handleVerifySuccess = useCallback(async () => {
    clearPersistedState()
  }, [])

  return {
    subStep,
    currentMode,
    email,
    phoneE164,
    identifier,
    isPhoneLoginEnabled: phoneEnabled,
    isSubmitting,
    cooldownSeconds: activeCooldownSeconds,
    isCooldownActive: activeCooldownSeconds > 0,
    errorMessage,
    fieldError,
    setMode,
    setEmail: updateEmail,
    setPhone,
    handleBlur,
    handleSubmit,
    backToIdentifier,
    handleVerifySuccess,
  }
}
